<template>
    <div class="card" v-show="reportsLoaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.astral-import') }}</h3>
                </div>
                <div class="col-6 text-right">

                    <base-button type="danger" size="sm mr-0 ml-1 mb-1" @click="deleteAllReports"
                        v-if="manyReports && !makingNewExport"
                    >
                        <span class="btn-inner--icon"><i class="tim-icons icon-simple-remove"></i></span>
                        <span class="btn-inner--text">{{ $t('astral-import.delete-all-reports') }}</span>
                    </base-button>

                    <base-button type="danger" size="sm mr-0 ml-1 mb-1" @click="deleteReport"
                        v-if="selectedReportId && !makingNewExport"
                    >
                        <span class="btn-inner--icon"><i class="tim-icons icon-simple-remove"></i></span>
                        <span class="btn-inner--text">{{ $t('astral-import.delete-report') }}</span>
                    </base-button>

                    <base-button type="primary" size="sm mr-0 ml-1 mb-1" :loading="makingNewExport" @click="newExport">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('astral-import.new-import') }}</span>
                    </base-button>

                </div>
            </div>
        </div>

        <div class="pt-2">

            <p class="mb-4 px-4" v-if="noReport">{{ $t('astral-import.no-report') }}</p>
            
            <base-select class="mb-4 px-4" v-else
                :placeholder="$t('astral-import.select-report')"
                v-model="selectedReportId"
                :options="reports"
                :notes="reportNotes"
                @input="selectReport"
            >
            </base-select>

            <base-alert type="danger" class="mb-4 mx-4" v-if="selectedReport && selectedReport.error">
                <i class="fas fa-exclamation-triangle mr-3"></i> <b>ERROR</b>
                <p class="mb-0"><small>{{ selectedReport.error }}</small></p>
            </base-alert>

            <el-table v-if="!noReport"
                class="table-responsive table-flush"
                header-row-class-name="thead-light"
                :data="ctrlOperations.rows"
                ref="table">

                <el-table-column :label="$t('astral-import.collection')">
                    <template v-slot="{row}">
                        <b>{{ row.collection }}</b>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('astral-import.count')">
                    <template v-slot="{row}">
                        <div v-html="countHtml(row.count)">
                        </div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('astral-import.status')">
                    <template v-slot="{row}">
                        <badge type="success" v-if="row.status == 'passed'">{{ row.status }}</badge>
                        <badge type="danger" v-else>{{ row.status }}</badge>
                    </template>
                </el-table-column>
            </el-table>

        </div>

        <portal to="modals">

            <confirm-modal v-model="ctrlReports.modals.confirmDelete">
                <p>{{ $t('astral-import.delete-report-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrlReports.modals.confirmDeleteAll">
                <p>{{ $t('astral-import.delete-all-reports-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrlReports.modals.confirmExport">
                <p>{{ $t('astral-import.new-import-confirm') }}</p>
            </confirm-modal>
            
        </portal>
    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import moment from 'moment'

    export default {

        data() {
            return {
                moment: moment,
                reportsLoaded: false,
                operationsLoaded: false,
                makingNewExport: false,
                selectedReportId: null,
                ctrlReports: new CrudController({
                    endpoint: '/socrates/api/front/astral-import/reports',
                    mapData: this.mapReportData,
                    unmapData: this.unmapData,
                    modals: ['confirmExport', 'confirmDeleteAll'],
                    callbacks: {
                        listed: this.reportsListed
                    },
                }),
                ctrlOperations: new CrudController({
                    endpoint: '/socrates/api/front/astral-import/operations',
                    mapData: this.mapOperationData,
                    unmapData: this.unmapData,
                    query: {
                        filters: { report_id: this.selectedReportId }
                    },
                }),
            }
        },

        created() {
            this.ctrlReports.init(this, 'ctrlReports').then(resp => {
                this.reportsLoaded = true
            })
        },

        computed: {

            noReport() {
                return this.ctrlReports.rows.length == 0
            },

            manyReports() {
                return this.ctrlReports.rows.length > 1
            },

            selectedReport() {
                return this.ctrlReports.rows.find(report => report.id == this.selectedReportId)
            },

            reports() {
                let reports = {}
                for (let report of this.ctrlReports.rows) {
                    reports[report.id] = this.reportDate(report)
                }
                return reports
            },

            reportNotes() {
                let reports = {}
                for (let report of this.ctrlReports.rows) {
                    reports[report.id] = report.mode + ' / ' + report.status
                }
                return reports
            },
        },

        methods: {

            countHtml(counts) {
                let html = ''
                for (let count of counts) {
                    if (count.value == 0) {
                        continue
                    }
                    html += '<p class="mb-0 text-' + this.countStyle(count.type) + '"><small>' + count.type + ': <b>' + count.value + '</b></small></p>';
                }
                return html
            },

            countStyle(type) {
                return {
                    source: 'black',
                    updated: 'success',
                    linked: 'success',
                    created: 'success',
                    deleted: 'success',
                    update_ignored: 'warning',
                    link_ignored: 'warning',
                    create_ignored: 'warning',
                    delete_ignored: 'warning',
                    delete_marked: 'warning',
                    delete_aborted: 'warning',
                }[type]
            },

            reportsListed() {
                if (this.ctrlReports.rows.length > 0) {
                    this.selectReport(this.ctrlReports.rows.slice(-1)[0].id)
                } else {
                    this.selectReport(null)
                }
            },
            
            selectReport(reportId) {
                this.selectedReportId = reportId
                this.ctrlOperations.query.filters.report_id = this.selectedReportId

                this.ctrlOperations.init(this, 'ctrlOperations').then(resp => {
                    this.operationsLoaded = true
                })
            },
            
            deleteReport() {
                this.ctrlReports.delete(this.selectedReportId)
            },

            deleteAllReports() {
                this.ctrlReports.modals.open('confirmDeleteAll').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.delete('/socrates/api/front/astral-import/reports')
                        .then(resp => {
                            this.ctrlReports.list()
                        })
                        .catch(err => {
                            this.$auth.handleErrorVm(err, this)
                        })        
                    }
                })
            },

            newExport() {
                this.ctrlReports.modals.open('confirmExport').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.makingNewExport = true
                        axios.post('/socrates/api/front/astral-import/export')
                        .then(resp => {
                            this.makingNewExport = false
                            this.ctrlReports.list()
                        })
                        .catch(err => {
                            this.$auth.handleErrorVm(err, this)
                        })        
                    }
                })
            },

            reportDate(report) {
                return this.moment(report.updated_at).format('YYYY/MM/DD HH:mm')
            },

            mapReportData(report) {
                return {
                    id: report ? report.id : null,
                    mode: report ? report.mode : null,
                    status: report ? report.status : null,
                    error: report ? report.error : null,
                    updated_at: report ? report.updated_at : null,
                }
            },

            mapOperationData(operation) {
                return {
                    id: operation ? operation.id : null,
                    collection: operation ? operation.collection : null,
                    count: operation ? operation.count : null,
                    status: operation ? operation.status : null,
                    error: operation ? operation.error : null,
                }
            },

            unmapData(data) {
                return data
            }
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.last,
    .card >>> td.last {
        padding-right: 1.5rem;
    }

</style>
